/**
 * 天气组件
 * 显示当前天气信息，带有优雅的加载动画
 */

import React, { useState, useEffect } from 'react';
import { weatherApi } from '../../network/index.js';
import { motion } from 'framer-motion';

/**
 * 天气代码映射
 */
const WEATHER_CODES = {
  0: { icon: '☀️', description: '晴朗' },
  1: { icon: '🌤️', description: '大部分晴朗' },
  2: { icon: '⛅', description: '部分多云' },
  3: { icon: '☁️', description: '阴天' },
  45: { icon: '🌫️', description: '雾' },
  48: { icon: '🌫️', description: '雾凇' },
  51: { icon: '🌦️', description: '小雨' },
  53: { icon: '🌧️', description: '中雨' },
  55: { icon: '🌧️', description: '大雨' },
  56: { icon: '🌨️', description: '冻雨' },
  57: { icon: '🌨️', description: '冻雨' },
  61: { icon: '🌦️', description: '小雨' },
  63: { icon: '🌧️', description: '中雨' },
  65: { icon: '🌧️', description: '大雨' },
  66: { icon: '🌨️', description: '冻雨' },
  67: { icon: '🌨️', description: '冻雨' },
  71: { icon: '❄️', description: '小雪' },
  73: { icon: '🌨️', description: '中雪' },
  75: { icon: '🌨️', description: '大雪' },
  77: { icon: '❄️', description: '雪粒' },
  80: { icon: '🌦️', description: '阵雨' },
  81: { icon: '🌧️', description: '阵雨' },
  82: { icon: '🌧️', description: '强阵雨' },
  85: { icon: '🌨️', description: '阵雪' },
  86: { icon: '🌨️', description: '强阵雪' },
  95: { icon: '⛈️', description: '雷暴' },
  96: { icon: '⛈️', description: '雷暴伴冰雹' },
  99: { icon: '⛈️', description: '强雷暴伴冰雹' }
};

/**
 * 获取天气信息
 * @param {number} code - 天气代码
 * @returns {Object} 天气信息
 */
const getWeatherInfo = (code) => {
  return WEATHER_CODES[code] || { icon: '❓', description: '未知' };
};

/**
 * 天气组件
 * @param {Object} props - 组件属性
 * @param {string} props.className - 自定义样式类
 * @param {Object} props.location - 位置信息
 * @param {number} props.location.latitude - 纬度
 * @param {number} props.location.longitude - 经度
 * @param {string} props.location.name - 位置名称
 */
function WeatherWidget({ className = '', location = null }) {
  const [weatherData, setWeatherData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(location || {
    latitude: 39.9042,
    longitude: 116.4074,
    name: '北京'
  });

  /**
   * 获取用户地理位置
   */
  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            // 通过坐标获取城市名称
            const geoData = await weatherApi.getGeoLocation(`${latitude},${longitude}`);
            const cityName = geoData.results?.[0]?.name || '当前位置';
            setCurrentLocation({ latitude, longitude, name: cityName });
          } catch (err) {
            console.warn('获取城市名称失败:', err);
            setCurrentLocation({ latitude, longitude, name: '当前位置' });
          }
        },
        (error) => {
          console.warn('获取地理位置失败:', error);
          // 使用默认位置（北京）
        }
      );
    }
  };

  /**
   * 获取天气数据
   */
  const fetchWeatherData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await weatherApi.getCurrentWeather({
        latitude: currentLocation.latitude,
        longitude: currentLocation.longitude
      });
      setWeatherData(data);
    } catch (err) {
      console.error('获取天气数据失败:', err);
      setError('获取天气数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 组件挂载时获取用户位置和天气数据
  useEffect(() => {
    if (!location) {
      getUserLocation();
    }
  }, [location]);

  // 位置变化时获取天气数据
  useEffect(() => {
    if (currentLocation) {
      fetchWeatherData();
    }
  }, [currentLocation]);

  if (loading) {
    return (
      <motion.div 
        className={`bg-card border border-border rounded-lg p-4 ${className}`}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-muted rounded-full animate-pulse flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
          <div className="space-y-2 flex-1">
            <div className="h-4 bg-muted rounded animate-pulse"></div>
            <div className="h-3 bg-muted rounded w-2/3 animate-pulse"></div>
          </div>
        </div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div 
        className={`bg-card border border-border rounded-lg p-4 ${className}`}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center space-x-3 text-destructive">
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex-1">
            <p className="text-sm font-medium">{error}</p>
            <button 
              onClick={fetchWeatherData}
              className="text-xs text-primary hover:text-primary/80 underline mt-1"
            >
              重试
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  if (!weatherData?.current) {
    return (
      <motion.div 
        className={`bg-card border border-border rounded-lg p-4 ${className}`}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="text-center text-muted-foreground">
          <span className="text-sm">暂无天气数据</span>
        </div>
      </motion.div>
    );
  }

  const { current } = weatherData;
  const weatherInfo = getWeatherInfo(current.weather_code);
  const temperature = Math.round(current.temperature_2m);
  const feelsLike = Math.round(current.apparent_temperature);
  const humidity = current.relative_humidity_2m;
  const windSpeed = Math.round(current.wind_speed_10m);

  return (
    <motion.div 
      className={`bg-card border border-border rounded-lg p-4 ${className}`}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <motion.div 
            className="text-3xl"
            initial={{ rotate: -10, scale: 0.8 }}
            animate={{ rotate: 0, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {weatherInfo.icon}
          </motion.div>
          <div>
            <div className="flex items-baseline space-x-1">
              <span className="text-2xl font-bold text-foreground">{temperature}°</span>
              <span className="text-sm text-muted-foreground">C</span>
            </div>
            <p className="text-sm text-muted-foreground">{weatherInfo.description}</p>
          </div>
        </div>
        
        <div className="text-right">
          <div className="flex items-center space-x-1 text-muted-foreground mb-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="text-xs">{currentLocation.name}</span>
          </div>
          <div className="space-y-1 text-xs text-muted-foreground">
            <div className="flex items-center space-x-1">
              <span>体感 {feelsLike}°</span>
            </div>
            <div className="flex items-center space-x-2">
              <span>湿度 {humidity}%</span>
              <span>风速 {windSpeed}km/h</span>
            </div>
          </div>
        </div>
      </div>

      {/* 今日温度趋势 */}
      {weatherData.daily && (
        <div className="mt-3 pt-3 border-t border-border">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>今日</span>
            <div className="flex items-center space-x-2">
              <span>最高 {Math.round(weatherData.daily.temperature_2m_max[0])}°</span>
              <span>最低 {Math.round(weatherData.daily.temperature_2m_min[0])}°</span>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default WeatherWidget;